import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { isNil } from 'ramda';
import type { Preset } from '../../../types/Api';
const PRESET_WRAPPER_HEIGHT = 36;
type UsePresetOverflowProps = {
  presets: Preset[] | undefined;
};
type UsePresetOverflowResult = {
  visibleCount: number | undefined;
  wrapperWidth: number;
  buttonsWrapperRef: RefObject<HTMLDivElement | null>;
  buttonsGhostRef: RefObject<HTMLDivElement | null>;
};

/**
 * Measures preset button overflow using a ResizeObserver on a ghost div.
 *
 * The ghost div mirrors the visible preset buttons at full width but is hidden
 * from users. When its size changes the observer recalculates how many buttons
 * fit within PRESET_WRAPPER_HEIGHT and updates visibleCount accordingly.
 */
export function usePresetOverflow(_props: UsePresetOverflowProps): UsePresetOverflowResult {
  const buttonsWrapperRef = useRef<HTMLDivElement>(null);
  const buttonsGhostRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>();
  /**
   * wrapperWidth is kept as state so consumers can read it reactively without
   * accessing buttonsWrapperRef.current during render (which React Compiler flags
   * as "Cannot access refs during render").
   */
  const [wrapperWidth, setWrapperWidth] = useState(0);

  /**
   * @mandatory useCallback with empty deps is required here: this function is captured
   * inside the useOnMount callback that creates ResizeObservers.
   * Without stable identity, the closure would capture a stale version.
   * setVisibleCount is stable (React setState setter), no captured closure vars.
   */
  const calculateVisibleItemCount = useCallback((ghostParent: HTMLElement | null) => {
    if (isNil(ghostParent)) {
      return;
    }
    let overflowDetected = false;
    for (const [index, child] of (Array.from(ghostParent.children) as HTMLElement[]).entries()) {
      const childBottom = child.offsetTop + child.offsetHeight;
      if (childBottom > PRESET_WRAPPER_HEIGHT) {
        setVisibleCount(index);
        overflowDetected = true;
        break;
      }
    }
    if (!overflowDetected) {
      // Count DOM children directly — avoids depending on props.presets reference,
      // which changes on every API refetch even when preset count is unchanged.
      setVisibleCount(ghostParent.children.length);
    }
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- intentional: cleanup-only useEffect with stable dep; useOnMount does not support cleanup returns; calculateVisibleItemCount is useCallback([]) so this fires once on mount and tears down on unmount
  useEffect(() => {
    const wrapperObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (buttonsGhostRef.current && buttonsWrapperRef.current) {
          const width = buttonsWrapperRef.current.offsetWidth;
          buttonsGhostRef.current.style.width = `${width}px`;
          setWrapperWidth(width);
        }
      });
    });
    if (buttonsWrapperRef.current) {
      wrapperObserver.observe(buttonsWrapperRef.current);
    }
    const ghostObserver = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        calculateVisibleItemCount(entries[0].target as HTMLElement);
      });
    });
    if (buttonsGhostRef.current) {
      ghostObserver.observe(buttonsGhostRef.current);
    }
    return () => {
      ghostObserver.disconnect();
      wrapperObserver.disconnect();
    };
  }, [calculateVisibleItemCount]);
  return {
    visibleCount,
    wrapperWidth,
    buttonsWrapperRef,
    buttonsGhostRef
  };
}